import React, { useEffect, useMemo, useRef, useState } from 'react';

import useProcedureStore from '../../../stores/procedureStore';
import { excludeLabTests } from '../../../constants/procedures';
import useSittingStore from '../../../stores/sittingStore';
import useOrgSettingsStore from '../../../stores/orgSettingsStore';
import { createPlan } from '../../../services/api/patientPlanApi';
import { usePermissions } from '../../../context/PermissionContext';
import { procedurePickerDeptAnchor } from '../../visit/departmentResolution';
import ValidityControl, { plusDaysISO } from '../../common/ValidityControl';
import useChartType from '../../../charts/core/useChartType';
import { useDraggable } from '../../../widgets';
import eventBus from '../../../services/realtime/EventBus';
import AssigneePicker from './AssigneePicker';
import ToothNumberInput from './dental/ToothNumberInput';
import { defaultDentition } from './dental/dentition';
// A2-S6 — shared department/role guard error mapping (single source of truth,
// also consumed by AttachPlanModal). Re-exported below for existing importers.
import { addProcedureErrorMessage } from '../assigneeGuardError';

export { addProcedureErrorMessage };

/**
 * QuickSearchAdd — typeahead + inline-confirm shipped inside the half-disc
 * widget. Replaces the AddProcedureModal multi-step wizard for the common
 * single-procedure path.
 *
 * Steps:
 *   1) Type to filter the catalog (active procedures only).
 *   2) Pick a row → expand a 3-field inline panel: timing (today / day-N),
 *      assignee defaulted to self, optional cost override.
 *   3) Single Confirm → POST /plans (single-item plan).
 *
 * Catalog comes from useProcedureStore (cached, fetched once on mount).
 *
 * Props:
 *   visit             — { visit_id, patient_id }
 *   viewer            — { memberId, role }
 *   onCreated()       — fires after successful create so the chip list
 *                       can refetch the visit's sittings.
 *   palette           — theme tokens from the disc widget (bg, text,
 *                       muted, accent, border)
 */

const TIMING_OPTIONS = [
  { value: 'now',          label: 'Today' },
  { value: 'later_today',  label: 'Later today' },
  { value: '+1d',          label: '+1 day' },
  { value: '+7d',          label: '+7 days' },
  { value: '+14d',         label: '+14 days' },
];

function offsetISODate(daysFromToday) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + daysFromToday);
  return d.toISOString().slice(0, 10);
}

function buildScheduledFor(timingChoice) {
  if (timingChoice === 'now' || timingChoice === 'later_today') return null;
  const m = /^\+(\d+)d$/.exec(timingChoice);
  if (!m) return null;
  return `${offsetISODate(parseInt(m[1], 10))}T00:00:00`;
}

function timingModeFor(timingChoice) {
  if (timingChoice === 'now') return 'now';
  if (timingChoice === 'later_today') return 'later_today';
  return 'scheduled';
}

/**
 * Phase 3 — Each search result is its own component so it can call
 * `useDraggable` for the row's payload. Clicking still picks (existing
 * UX); dragging starts the chart-drop flow. Hook can't be called from
 * inside a `.map(...)` body so this wrapper exists.
 */
function ProcedureSearchRow({ row, palette, onPick }) {
  const dragProps = useDraggable({
    type: 'procedure',
    source: 'rail-search',
    procedureId: row.procedure_id,
    procedureName: row.name,
    defaultCost: row.default_cost,
    isToothSpecific: !!row.is_tooth_specific,
  });
  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        {...dragProps}
        className="w-full text-left hover:bg-slate-100 dark:hover:bg-slate-700"
        title={`${row.name} — click to pick, drag onto a tooth to start linked to that tooth's diagnosis`}
        style={{
          padding: '0.55rem 0.75rem',
          background: 'transparent',
          border: 'none',
          borderBottom: `1px solid ${palette.border}`,
          color: palette.text,
          fontSize: '0.8125rem',
          fontWeight: 500,
          // `cursor: grab` hints draggability; on click the cursor flips
          // to pointer via the default user-agent behaviour, then to
          // `grabbing` on mousedown via :active styles in component CSS.
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
          userSelect: 'none',
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.name}
        </span>
        <span style={{ fontSize: '0.75rem', fontWeight: 700, opacity: 0.8, fontVariantNumeric: 'tabular-nums' }}>
          ₹{Number(row.default_cost || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
        </span>
      </button>
    </li>
  );
}

export default function QuickSearchAdd({
  visit,
  viewer,
  onCreated,
  palette,
  autoFocus = false,
  // When the user clicked "→ Plan procedure for this" on a diagnosis
  // pill in the chart, the parent passes the diagnosis + tokens so
  // this widget pre-fills them.
  prefilledDiagnosisMetaId = null,
  prefilledDiagnosisSummary = null,
  prefilledTokens = null,
  // Phase 2.A.4 — race defense. Each `sdk.openWidget('procedure', ...)`
  // call carries a UUID; ProcedureWidget / ChartPaneProcedurePicker
  // forwards it as this prop. Including it in the prefill-effect
  // dependencies ensures a rapid second click with the SAME tokens but
  // a NEW request_id still re-applies the prefill (instead of React
  // eliding the effect because shallow-equal deps).
  prefillRequestId = null,
  // Phase 3 — drag-and-drop. When the dentist drags a procedure chip
  // from the rail onto a tooth, the drop handler passes the procedure's
  // catalog id here so this widget auto-selects it instead of making
  // the dentist type/search again.
  prefilledProcedureId = null,
}) {
  // A4/S3 — the widget picker reads the SERVER-scoped slice for THIS visit's
  // department (dept-tagged ∪ org-wide), NOT the admin catalog. Anchor =
  // visit.department_id; falsy/EMERGENCY → null → full list.
  const procedures = useProcedureStore((s) => s.pickerProcedures);
  const fetchProceduresForDept = useProcedureStore((s) => s.fetchProceduresForDept);
  const pickerDeptAnchor = procedurePickerDeptAnchor(visit);
  // Org-wide skip-payment master — when on, every procedure is "no charge".
  const skipPaymentAll = useOrgSettingsStore((s) => s.skipPaymentAll);
  const fetchOrgSettings = useOrgSettingsStore((s) => s.fetchProcedurePaymentSettings);
  // Posts the auto-note as the first message in the procedure's
  // conversational thread (ProcedureNoteDialog reads from this same
  // store), so the team sees the diagnosis + tooth context when they
  // open notes.
  const addSittingNote = useSittingStore((s) => s.addSittingNote);
  const { hasPermission } = usePermissions();
  const canApplyDiscount = hasPermission?.('APPLY_DISCOUNT');

  const isDentist = useChartType() === 'dental';

  // Scoped catalog fetch keyed on the (primitive) dept anchor. Single-flight in
  // the store (which resolves — never rejects), so effect re-fires for the same
  // dept can't storm requests.
  useEffect(() => {
    fetchProceduresForDept(pickerDeptAnchor);
  }, [pickerDeptAnchor, fetchProceduresForDept]);

  useEffect(() => { fetchOrgSettings(); }, [fetchOrgSettings]);

  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState(null);   // ClinicProcedure row
  // Skip is effective when the clinic skips all procedures OR the picked
  // procedure is flagged skip_payment. Then nothing is charged and the
  // cost field is locked at ₹0 (no doctor override).
  const effectiveSkip = skipPaymentAll || !!picked?.skip_payment;
  const [timing, setTiming] = useState('now');
  const [costOverride, setCostOverride] = useState('');
  // W2/T3 — quantity (int >=1, default 1). Cost above is PER UNIT; the server
  // computes final_cost = quantity × unit_cost − discount (POST
  // /organisation/plans frozen contract) — client totals are display-only.
  const [quantity, setQuantity] = useState(1);
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [assigneeId, setAssigneeId] = useState('');   // '' → self
  // Doctor-controlled "needs my approval on complete" flag. Defaults to
  // unselected regardless of the catalog row's `requires_approval_default`
  // — the doctor chooses per assignment.
  const [requiresApproval, setRequiresApproval] = useState(false);
  // Expiry window — "Valid until" date (default advised + 30d) and the
  // standing-order ("No expiry till review date") toggle.
  const [expiresAt, setExpiresAt] = useState(plusDaysISO(30));
  const [isStanding, setIsStanding] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [busy, setBusy] = useState(false);
  // Phase 3.1 — tooth tokens for is_tooth_specific picks.
  const [toothNumbers, setToothNumbers] = useState([]);
  // meta_id of the diagnosis this procedure addresses, or null. Seeded
  // from the `prefilledDiagnosisMetaId` prop when the chart opens this
  // widget.
  const [linkedDiagnosisMetaId, setLinkedDiagnosisMetaId] = useState(null);
  // When the chart fires the prefill event it carries the diagnosis
  // summary too. We persist that string on the sitting row as `notes`
  // so the chart's findings panel can show exactly what was diagnosed
  // when surfacing the linked procedure.
  const [linkedDiagnosisSummary, setLinkedDiagnosisSummary] = useState('');
  const inputRef = useRef(null);

  // Seed pre-filled state when the parent passes a diagnosis to plan for.
  // F7.1 — also seed `linkedDiagnosisSummary` from the prop so the
  // chart's inline picker (which passes summary via props, not via the
  // legacy `arogya:plan-procedure-prefill` window event) gets the
  // auto-note pre-filled. Previously the prop was ignored and the
  // textarea stayed empty, which meant `notes` was never sent.
  useEffect(() => {
    if (prefilledDiagnosisMetaId) {
      setLinkedDiagnosisMetaId(prefilledDiagnosisMetaId);
    }
    if (prefilledDiagnosisSummary) {
      setLinkedDiagnosisSummary(String(prefilledDiagnosisSummary));
    }
    if (Array.isArray(prefilledTokens) && prefilledTokens.length > 0) {
      setToothNumbers(prefilledTokens);
    }
    // `prefillRequestId` is intentionally part of the dependency list
    // (Phase 2.A.4) so back-to-back clicks with identical tokens still
    // re-apply — last write wins.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledDiagnosisMetaId, prefilledDiagnosisSummary, prefilledTokens?.length, prefillRequestId]);

  // Phase 3 — drag-drop auto-pick. When the chart drops a procedure
  // onto a tooth, the drop handler passes `prefilledProcedureId`. As
  // soon as the catalog is loaded (or if already loaded on mount), the
  // matching catalog row is auto-selected so the dentist skips the
  // type-and-search step entirely. `prefillRequestId` keyed so back-to-
  // back drops of the same procedure on different teeth still re-apply.
  useEffect(() => {
    if (!prefilledProcedureId) return;
    if (!Array.isArray(procedures) || procedures.length === 0) return;
    if (picked && picked.procedure_id === prefilledProcedureId) return;
    const match = procedures.find((p) => p.procedure_id === prefilledProcedureId);
    if (match) {
      // Reuse the same pick path the click flow uses so cost / discount
      // defaults are applied consistently.
      handlePick(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledProcedureId, prefillRequestId, procedures?.length]);

  useEffect(() => {
    if (autoFocus && !picked) inputRef.current?.focus();
  }, [autoFocus, picked]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const active = excludeLabTests(procedures).filter((p) => p.active !== false);
    if (!q) return active.slice(0, 8);
    return active
      .filter((p) =>
        (p.name || '').toLowerCase().includes(q)
        || (p.category || '').toLowerCase().includes(q)
        || (p.specialty || '').toLowerCase().includes(q)
        || (p.ada_code || '').toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [query, procedures]);

  const reset = () => {
    setQuery('');
    setPicked(null);
    setTiming('now');
    setCostOverride('');
    setQuantity(1);
    setDiscountAmount('');
    setDiscountReason('');
    setAssigneeId('');
    setRequiresApproval(false);
    setExpiresAt(plusDaysISO(30));
    setIsStanding(false);
    setSubmitError('');
    setToothNumbers([]);
    setLinkedDiagnosisMetaId(null);
    setLinkedDiagnosisSummary('');
  };

  const handlePick = (row) => {
    setPicked(row);
    setCostOverride(String(row.default_cost ?? ''));
    setQuantity(1);
    setDiscountAmount('');
    setDiscountReason('');
    setAssigneeId('');
    setRequiresApproval(false);
    setSubmitError('');
    // Preserve pre-filled tokens + diagnosis from a chart-driven open;
    // otherwise start clean.
    if (!Array.isArray(prefilledTokens) || prefilledTokens.length === 0) {
      setToothNumbers([]);
    }
    if (!prefilledDiagnosisMetaId) {
      setLinkedDiagnosisMetaId(null);
    }
  };

  // W2/T3 — qty stepper (RegistrationProceduresSection pattern): default 1,
  // min 1, integer only. `bumpQty` (+/- buttons) always lands on a valid
  // integer by construction; `setQty` (typed input) rejects 0/blank/negative/
  // non-numeric by falling back to the current quantity.
  const bumpQty = (delta) => setQuantity((q) => Math.max(1, q + delta));
  const setQty = (raw) => {
    const n = parseInt(raw, 10);
    setQuantity((prev) => (Number.isInteger(n) && n >= 1 ? n : prev));
  };

  const handleConfirm = async () => {
    if (!picked || busy) return;
    const discountNumber = Number(discountAmount || 0);
    const costNumber = Number(costOverride || picked.default_cost || 0);
    if (discountNumber < 0) {
      setSubmitError('Discount cannot be negative.');
      return;
    }
    if (discountNumber > 0 && discountReason.trim().length < 3) {
      setSubmitError('Reason for discount is required (3+ chars).');
      return;
    }
    // Discount is a flat amount subtracted AFTER qty × unit (server:
    // final_cost = quantity × unit_cost − discount) — compare against the total.
    if (discountNumber > costNumber * quantity) {
      setSubmitError('Discount cannot exceed cost.');
      return;
    }
    // Phase 3.1 — catalog-flagged tooth-specific procedures must carry
    // at least one token.
    if (picked.is_tooth_specific && (!toothNumbers || toothNumbers.length === 0)) {
      setSubmitError('Pick at least one tooth (FDI / Q1–Q4 / FM).');
      return;
    }
    setBusy(true);
    setSubmitError('');
    const body = {
      patient_id: visit.patient_id,
      source_visit_id: visit.visit_id,
      procedure_id: picked.procedure_id,
      estimated_cost: String(costNumber || 0),
      discount_amount: String(discountNumber || 0),
      discount_reason: discountNumber > 0 ? discountReason.trim() : null,
      quantity,
      timing_mode: timingModeFor(timing),
      scheduled_for: buildScheduledFor(timing),
      assigned_to_member_id: assigneeId || viewer?.memberId || null,
      requires_approval: requiresApproval,
      // Expiry window — "Valid until" date (or the review date when standing).
      expires_at: expiresAt || null,
      standing: isStanding,
      remember_preferences: false,
      payment_timing: picked.payment_timing || null,
      shared_context: null,
      source: 'DOCTOR_ORIGINATED',
      // Phase 3.1 — only send tooth_numbers when the catalog row needs them.
      // Send tokens whenever the doctor picked any (dentist optional flow)
      // OR when the catalog row demands them.
      tooth_numbers: (toothNumbers && toothNumbers.length > 0)
        ? toothNumbers
        : null,
      // Soft reference to a diagnosis NOTE row when seeded from the
      // chart's "→ Plan procedure for this" prefill. Dentist-gated so
      // legacy callers don't accidentally write the field.
      ...(isDentist && linkedDiagnosisMetaId
        ? { linked_diagnosis_metadata_id: linkedDiagnosisMetaId }
        : {}),
      // F7.1 — Procedure notes. Now a first-class editable textarea in
      // the form (dentist sees + can edit before submit). No
      // role gate: any user who typed/auto-filled notes gets them
      // persisted. The backend silently accepts None when empty.
      ...(linkedDiagnosisSummary && linkedDiagnosisSummary.trim()
        ? { notes: linkedDiagnosisSummary.trim() }
        : {}),
    };
    const result = await createPlan(body);
    setBusy(false);
    if (result?.success) {
      // Kick the chart history hook to refresh so the newly-created
      // procedure paints its red marker on the affected tooth/surface
      // immediately, without waiting for the WS PLAN_CREATED broadcast
      // (which can race the createPlan response).
      // Pull the new plan + sitting IDs from the createPlan response.
      // Backend returns `PlanDetailResponse` (plan + items[] + sittings[]),
      // and apiClient wraps it under `data`. Fall back to top-level for
      // any non-standard wrapper.
      const createdPlanId = result?.data?.plan?.plan_id
        || result?.plan?.plan_id
        || null;
      const createdSittingId = result?.data?.sittings?.[0]?.sitting_id
        || result?.sittings?.[0]?.sitting_id
        || result?.data?.sitting?.sitting_id
        || result?.sitting?.sitting_id
        || null;

      try {
        // Phase 5 — EventBus.emit requires `{ type, payload }` (see
        // src/services/realtime/EventBus.js:136). The legacy 2-arg
        // signature silently fails the shape check, which is why the
        // chart used to stay stale after a drag-drop create until the
        // next manual refresh. Use the object form so dentalHistoryStore
        // (chart + summary) and sittingStore (widget rail) both react.
        eventBus.emit({
          type: 'TOOTH_CHART_REFRESH',
          payload: {
            patient_id: visit?.patient_id,
            visit_id: visit?.visit_id,
            procedure_id: picked.procedure_id,
            tooth_numbers: toothNumbers,
          },
        });
        // F7.2 — locally re-fire the SITTING_SCHEDULED lifecycle event
        // so the half-disc procedures list refreshes immediately,
        // without waiting for the websocket round-trip. The realtime
        // middleware in sittingStore dedupes if the WS event then
        // arrives. Source intentionally omitted so the middleware
        // processes it as a remote-equivalent (matches the recipe in
        // RowActionToolbar.emitSittingEvent + the new sittingStore
        // emitSittingLifecycle helper).
        eventBus.emit({
          type: 'SITTING_SCHEDULED',
          payload: {
            visit_id: visit?.visit_id,
            patient_id: visit?.patient_id,
            sitting_id: createdSittingId,
          },
        });
      } catch { /* best-effort */ }

      // Phase 5 — defensive belt: also force a per-visit refetch on the
      // widget rail's store so `sittingStore.byVisit[visitId]` picks up
      // the new sitting even if the middleware's source filter ever
      // drops the SITTING_SCHEDULED echo. force: must NOT piggyback a
      // coalesced in-flight fetch that predates this create, or the new
      // sitting would be missed. Mirrors RowActionToolbar.handleStart:146.
      if (visit?.visit_id) {
        try {
          await useSittingStore.getState().fetchForVisit(visit.visit_id, { force: true });
        } catch (_e) { /* rail stays at previous state; events still fired */ }
      }

      // F7.1+ — post the auto-note (diagnosis content + Tooth #N
      // prefix) as the FIRST message in the procedure's notes
      // thread. This is what ProcedureNoteDialog renders for the
      // team. Best-effort: if the post fails, we keep the procedure
      // (it was already created) and log a warning. The sitting.notes
      // field on the row is still populated separately via the
      // createPlan body, so the chart's tooltip still shows it.
      const trimmedNote = (linkedDiagnosisSummary || '').trim();
      if (trimmedNote && createdPlanId && createdSittingId) {
        try {
          const noteResult = await addSittingNote(
            createdPlanId,
            createdSittingId,
            trimmedNote,
          );
          if (!noteResult?.success) {
            // eslint-disable-next-line no-console
            console.warn(
              '[QuickSearchAdd] auto-note post failed:',
              noteResult?.error,
            );
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[QuickSearchAdd] auto-note post threw:', e);
        }
      }

      // Phase 3.1 telemetry.
      if (picked.is_tooth_specific) {
        // eslint-disable-next-line no-console
        console.info(
          'metric=tooth_specific_commit value=1 procedure=%s tokens=%d',
          picked.procedure_id, (toothNumbers || []).length,
        );
      }
      if ((picked.specialty || '') === 'finding') {
        // eslint-disable-next-line no-console
        console.info(
          'metric=finding_added value=1 procedure=%s', picked.procedure_id,
        );
      }
      reset();
      inputRef.current?.focus();
      if (typeof onCreated === 'function') await onCreated(result);
    } else {
      setSubmitError(addProcedureErrorMessage(result));
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.55rem 0.7rem',
    borderRadius: 10,
    border: `1px solid ${palette.border}`,
    background: palette.bg,
    color: palette.text,
    fontSize: '0.8125rem',
    outline: 'none',
  };

  const matchListStyle = {
    marginTop: '0.375rem',
    background: palette.bg,
    border: `1px solid ${palette.border}`,
    borderRadius: 12,
    maxHeight: 220,
    overflowY: 'auto',
    boxShadow: '0 8px 22px -12px rgba(16,24,40,0.25)',
  };

  if (picked) {
    // Inline expand — 3 fields + confirm.
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <strong style={{ flex: 1, fontSize: '0.8125rem' }}>{picked.name}</strong>
          <button
            type="button"
            onClick={reset}
            aria-label="Cancel add procedure"
            className="px-2 py-0.5 rounded-md border bg-white text-slate-700 hover:bg-slate-100
                       dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:border-slate-600"
            style={{ fontSize: '0.6875rem', borderColor: palette.border }}
          >
            ✕
          </button>
        </div>

        {/* Tooth-number step — shown when the catalog row is flagged
            is_tooth_specific (e.g. "RCT") and required. */}
        {picked.is_tooth_specific && (
          <div>
            <label style={{ fontSize: '0.6875rem', opacity: 0.8 }}>
              Tooth (FDI / quadrant / FM) *
            </label>
            <ToothNumberInput
              value={toothNumbers}
              onChange={setToothNumbers}
              dentitionMode={defaultDentition(visit?.patient_age)}
            />
          </div>
        )}

        {/* F7.1 — Procedure notes textarea. Auto-populated with the
            diagnosis content (with `Tooth #N` prefix) when the picker
            is opened from the chart's "+ Add procedure" / "→ Plan
            procedure for this" CTAs. Dentist can edit, clear, or
            ignore. Saved to `sitting.notes` on submit. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <label
            htmlFor="qs-notes"
            style={{
              fontSize: '0.6875rem',
              opacity: 0.85,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span>Procedure notes</span>
            {prefilledDiagnosisSummary && (
              <span style={{ opacity: 0.6, fontWeight: 'normal' }}>
                · auto-filled from diagnosis (editable)
              </span>
            )}
          </label>
          <textarea
            id="qs-notes"
            value={linkedDiagnosisSummary}
            onChange={(e) => setLinkedDiagnosisSummary(e.target.value)}
            rows={2}
            maxLength={5000}
            placeholder="Optional — what the procedure addresses, surfaces involved, materials, etc."
            style={{
              ...inputStyle,
              fontFamily: 'inherit',
              resize: 'vertical',
              minHeight: '2.5rem',
              lineHeight: 1.35,
            }}
          />
        </div>

        <div>
          <label htmlFor="qs-timing" style={{ fontSize: '0.6875rem', opacity: 0.8 }}>When</label>
          <select
            id="qs-timing"
            value={timing}
            onChange={(e) => setTiming(e.target.value)}
            style={{ ...inputStyle, padding: '0.35rem 0.5rem' }}
          >
            {TIMING_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <ValidityControl
          expiresAt={expiresAt}
          standing={isStanding}
          oneTimeOnly={!!picked?.one_time_only}
          oneTimeOnlyReason={picked?.one_time_only_reason}
          onChange={({ expiresAt: ea, standing }) => {
            setExpiresAt(ea);
            setIsStanding(standing);
          }}
        />

        {/* W2/T3 — Quantity stepper. Cost above/below is PER UNIT; total
            shown here is display-only (server computes final_cost = qty ×
            unit − discount and ignores any client total). */}
        <div>
          <label style={{ fontSize: '0.6875rem', opacity: 0.8, display: 'block', marginBottom: 2 }}>
            Quantity
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <button
              type="button"
              aria-label="Decrease quantity"
              onClick={() => bumpQty(-1)}
              disabled={quantity <= 1}
              style={{
                width: '1.5rem', height: '1.5rem', borderRadius: '0.375rem', lineHeight: 1,
                border: `1px solid ${palette.border}`, background: palette.bg, color: palette.text,
                cursor: quantity <= 1 ? 'not-allowed' : 'pointer', opacity: quantity <= 1 ? 0.5 : 1,
              }}
            >−</button>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={quantity}
              aria-label="Quantity"
              onChange={(e) => setQty(e.target.value)}
              style={{ ...inputStyle, width: '2.5rem', textAlign: 'center', padding: '0.25rem' }}
            />
            <button
              type="button"
              aria-label="Increase quantity"
              onClick={() => bumpQty(1)}
              style={{
                width: '1.5rem', height: '1.5rem', borderRadius: '0.375rem', lineHeight: 1,
                border: `1px solid ${palette.border}`, background: palette.bg, color: palette.text,
                cursor: 'pointer',
              }}
            >+</button>
            {!effectiveSkip && (
              <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>
                Total: ₹{(Number(costOverride || 0) * quantity).toLocaleString('en-IN')}
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="qs-cost" style={{ fontSize: '0.6875rem', opacity: 0.8 }}>
              Cost (₹)
            </label>
            <input
              id="qs-cost"
              type="number"
              min="0"
              step="1"
              value={effectiveSkip ? 0 : costOverride}
              onChange={(e) => setCostOverride(e.target.value)}
              disabled={effectiveSkip}
              title={effectiveSkip
                ? 'No charge — payment is skipped for this procedure'
                : undefined}
              style={{ ...inputStyle, opacity: effectiveSkip ? 0.6 : 1 }}
            />
          </div>
          {canApplyDiscount && !effectiveSkip && (
            <div style={{ flex: 1 }}>
              <label htmlFor="qs-discount" style={{ fontSize: '0.6875rem', opacity: 0.8 }}>
                Discount (₹)
              </label>
              <input
                id="qs-discount"
                type="number"
                min="0"
                step="1"
                value={discountAmount}
                onChange={(e) => setDiscountAmount(e.target.value)}
                placeholder="0"
                style={inputStyle}
              />
            </div>
          )}
        </div>

        {canApplyDiscount && Number(discountAmount || 0) > 0 && (
          <div>
            <label htmlFor="qs-reason" style={{ fontSize: '0.6875rem', opacity: 0.8 }}>
              Reason for discount
            </label>
            <input
              id="qs-reason"
              type="text"
              value={discountReason}
              onChange={(e) => setDiscountReason(e.target.value)}
              placeholder="e.g. senior citizen"
              maxLength={120}
              style={inputStyle}
            />
          </div>
        )}

        {/* A2-S6 follow-up — dept-scope the "Assign to" candidate list to the
            visit's STAMPED department of care (frozen at registration), same
            rule as the reassign picker + registration. Anchor = visit.department_id
            (derive-from-visit); falsy → no filter (org with 0 depts, no/emergency
            dept). Display-only; the server re-validates on submit. */}
        <AssigneePicker
          value={assigneeId}
          onChange={setAssigneeId}
          viewer={viewer}
          palette={palette}
          visitDepartmentId={visit?.department_id || null}
        />

        {/* Doctor's approval gate. When checked, the sitting waits for
            the doctor to sign off before it can move to completed. */}
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            fontSize: '0.75rem',
            color: palette?.text || '#1f2937',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            checked={requiresApproval}
            onChange={(e) => setRequiresApproval(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          Needs my approval on complete
        </label>

        {/* No-payment hint — shown ONLY when payment is genuinely skipped
            for this procedure (clinic-wide skip, or this procedure's own
            skip flag). A plain ₹0 procedure that is NOT skip-flagged still
            goes through reception, so the hint must not show for it. */}
        {effectiveSkip && (
          <p
            style={{
              margin: 0,
              padding: '0.375rem 0.5rem',
              borderRadius: 6,
              fontSize: '0.6875rem',
              background: 'rgba(13,148,136,0.10)',
              color: '#0f766e',
              border: '1px solid rgba(13,148,136,0.25)',
            }}
          >
            ✓ No payment needed — goes straight to the assignee's queue.
          </p>
        )}

        {submitError && (
          <p style={{ margin: 0, fontSize: '0.6875rem', color: '#b91c1c' }}>
            {submitError}
          </p>
        )}

        <button
          type="button"
          onClick={handleConfirm}
          disabled={busy}
          className="rounded-md bg-teal-600 text-white text-sm font-semibold py-1.5
                     hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {busy ? 'Adding…' : 'Add procedure'}
        </button>
      </div>
    );
  }

  return (
    <div>
      <label
        htmlFor="qs-search"
        style={{ display: 'block', fontSize: '0.625rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.6, marginBottom: 5 }}
      >
        Add procedure
      </label>
      <div style={{ position: 'relative' }}>
        <input
          id="qs-search"
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search catalog…"
          style={{ ...inputStyle, paddingRight: query ? '1.75rem' : inputStyle.padding }}
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(''); inputRef.current?.focus(); }}
            aria-label="Clear search"
            title="Clear"
            style={{
              position: 'absolute',
              right: 6,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 18, height: 18,
              borderRadius: 9,
              border: 'none',
              background: 'transparent',
              color: palette.muted,
              cursor: 'pointer',
              fontSize: '0.75rem',
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        )}
      </div>
      {matches.length > 0 && (
        <ul role="listbox" style={{ ...matchListStyle, listStyle: 'none', margin: '0.375rem 0 0', padding: 0 }}>
          {matches.map((row) => (
            <ProcedureSearchRow
              key={row.procedure_id}
              row={row}
              palette={palette}
              onPick={() => handlePick(row)}
            />
          ))}
        </ul>
      )}
      {query.trim() && matches.length === 0 && (
        <p style={{ margin: '0.375rem 0 0', fontSize: '0.6875rem', opacity: 0.7 }}>
          No matching procedures.
        </p>
      )}
    </div>
  );
}
